import { useEffect, useState, type FormEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../api";
import { AIRCRAFT_QUALIFICATION_PACKAGES } from "../../shared/aircraft-qualification-packages";
import { MANAGEABLE_SKILLS } from "../../shared/domain-types";
import { isDateOnly } from "../../shared/date-only";
import type { MissionCatalogs, QualificationProfile, SessionPayload, VisaDeclaration } from "../types";
import { skillLabel } from "../utils";
import { Alert, Button, Field, SearchableCombobox, SelectField, TextareaField } from "../components/ui";

export function OnboardingScreen({ session, onDone }: {
  session: SessionPayload;
  onDone: () => Promise<void> | void;
}) {
  const user = session.user;
  const managementRole = user.role === "supervisor" || user.role === "manager";
  const [phone, setPhone] = useState(user.phone ?? "");
  const [hireDate, setHireDate] = useState(user.hireDate ?? "");
  const [annual, setAnnual] = useState(session.balances?.annual?.toString() ?? "");
  const [excuse, setExcuse] = useState(session.balances?.excuse?.toString() ?? "");
  const initialSkills = (user.skills ?? []).filter((skill) => (MANAGEABLE_SKILLS as readonly string[]).includes(skill));
  const [skills, setSkills] = useState<string[]>(initialSkills);
  const [profile, setProfile] = useState<QualificationProfile | null>(null);
  const [visas, setVisas] = useState<VisaDeclaration[]>([]);
  const [visasChanged, setVisasChanged] = useState(false);
  const [catalogs, setCatalogs] = useState<MissionCatalogs | null>(null);
  const [packageIds, setPackageIds] = useState<string[]>([]);
  const [restrictionNote, setRestrictionNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    setLoadError("");
    Promise.all([
      api<QualificationProfile>("/api/profile/qualifications"),
      api<MissionCatalogs>("/api/missions/catalogs"),
    ]).then(([saved, options]) => {
      if (!active) return;
      setProfile(saved);
      setVisas(saved.visas);
      setCatalogs(options);
    }).catch((caught) => {
      if (active) setLoadError(caught instanceof ApiError ? caught.message : "Mevcut bilgiler yüklenemedi.");
    });
    return () => { active = false; };
  }, [user.id, attempt]);
  function updateVisa(index: number, values: Partial<VisaDeclaration>) {
    setVisasChanged(true);
    setVisas((current) => current.map((visa, i) => i === index ? { ...visa, ...values } : visa));
  }
  async function submit(event?: FormEvent, skip = false) {
    event?.preventDefault();
    if (loading) return;
    setError("");
    if (!skip) {
      if (!profile || !catalogs) return setError("Bilgilerin yüklenmesini bekleyin veya şimdilik atlayın.");
      if (visasChanged && visas.some((visa) => !visa.countryCode || !isDateOnly(visa.validUntil)
        || (visa.validFrom && (!isDateOnly(visa.validFrom) || visa.validFrom > visa.validUntil)))) {
        return setError("Eklediğiniz vizenin ülkesini ve geçerli tarihlerini girin veya boş kaydı kaldırın. Başlangıç, bitiş tarihinden sonra olamaz.");
      }
      if (new Set(visas.map((visa) => visa.countryCode)).size !== visas.length) return setError("Her ülke için yalnızca bir vize ekleyin.");
      if (hireDate && !isDateOnly(hireDate)) return setError("Geçerli bir işe giriş tarihi seçin.");
      if (!managementRole && [annual, excuse].some((value) => value !== "" && (!Number.isFinite(Number(value)) || Math.abs(Number(value)) > 9999))) return setError("İzin bakiyesi -9999 ile 9999 arasında olmalıdır.");
    }
    setLoading(true);
    try {
      if (!skip) {
        if ([...skills].sort().join() !== [...initialSkills].sort().join()) {
          await api(`/api/personnel/${user.id}/skills`, { method: "PATCH", body: { skills } });
        }
        if (skills.includes("B1") && packageIds.length) {
          await api("/api/profile/qualifications/aircraft", { method: "POST", body: { packageIds, restrictionNote: restrictionNote.trim() || null } });
        }
        if (visasChanged) {
          await api("/api/profile/qualifications/visas", { method: "PUT", body: { visas: visas.map(({ countryCode, validFrom, validUntil, entryType, restrictionNote }) => ({ countryCode, validFrom: validFrom || null, validUntil, entryType, restrictionNote: restrictionNote || null })) } });
        }
        // Send only edited fields: reopening the form must not overwrite balances.
        await api("/api/profile", { method: "PATCH", body: {
          ...(phone !== (user.phone ?? "") ? { phone: phone.trim() || null } : {}),
          ...(!managementRole && hireDate !== (user.hireDate ?? "") ? { hireDate: hireDate || null } : {}),
          ...(!managementRole && annual !== (session.balances?.annual?.toString() ?? "") ? { annualBalance: annual === "" ? null : Number(annual) } : {}),
          ...(!managementRole && excuse !== (session.balances?.excuse?.toString() ?? "") ? { excuseBalance: excuse === "" ? null : Number(excuse) } : {}),
        } });
      }
      // Finish only after all requested saves succeed. Skip changes no profile data.
      await api("/api/profile", { method: "PATCH", body: { onboardingComplete: true } });
      await onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Bilgiler kaydedilemedi. Yeniden deneyebilirsiniz.");
    } finally { setLoading(false); }
  }
  return <main className="onboarding-page">
    <section className="onboarding-card">
      <span className="eyebrow">PROFİL BİLGİLERİNİZ</span>
      <h1>Profilinizi tamamlayın</h1>
      <p>Profil ekranımız genişledi. Yeni ve mevcut tüm kullanıcılarımızdan bilgilerini bir kez gözden geçirmelerini istiyoruz. Mevcut bilgileriniz korunur.</p>
      <Alert tone="info">Tüm bölümler isteğe bağlıdır. Vize ve uçak tipi / yetki bilgileriniz, olası görevlendirmelerde personel seçimi ve filtreleme amacıyla kullanılacaktır. Güncel bilgiler doğru planlamaya yardımcı olur. Daha sonra Profil bölümünden düzenleyebilirsiniz.</Alert>
      {loadError ? <Alert tone="danger">{loadError} <Button type="button" variant="ghost" onClick={() => setAttempt((value) => value + 1)}>Yeniden dene</Button></Alert> : null}
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <fieldset className="onboarding-fields" disabled={loading}>
          <legend>İletişim ve izin bilgileri</legend>
          <div className="form-grid">
            <Field label="Telefon (isteğe bağlı)" type="tel" value={phone} maxLength={40} onChange={(event) => setPhone(event.target.value)} placeholder="05xx xxx xx xx" />
            {!managementRole ? <>
              <Field label="İşe giriş tarihi (isteğe bağlı)" type="date" value={hireDate} onChange={(event) => setHireDate(event.target.value)} />
              <Field label="Kalan yıllık izin" type="number" step="0.25" min={-9999} max={9999} value={annual} onChange={(event) => setAnnual(event.target.value)} placeholder="Bilinmiyor" />
              <Field label="Kalan mazeret izni" type="number" step="0.25" min={-9999} max={9999} value={excuse} onChange={(event) => setExcuse(event.target.value)} placeholder="Bilinmiyor" />
            </> : null}
          </div>
          {!managementRole ? <p className="muted">Mevcut kalan izin tutarınızı girin. Bilmiyorsanız boş bırakabilirsiniz; izin talebinizi engellemez.</p> : null}
        </fieldset>
        <fieldset className="onboarding-fields" disabled={loading || !profile || !catalogs}>
          <legend>Yetkiler ve uçak tipleri (isteğe bağlı)</legend>
          <SearchableCombobox label="Herhangi bir yetkiniz / skill'iniz var mı?" multiple value={skills} options={MANAGEABLE_SKILLS.map((skill) => ({ value: skill, label: skillLabel(skill) }))} onChange={(value) => setSkills(value as string[])} placeholder="Varsa seçin; yoksa boş bırakın" />
          <p className="muted">Bu bilgiler personel beyanıdır; resmî yetkilendirme veya sertifikasyon anlamına gelmez. Uçak tipi eklemek için B1 yetkisini seçin.</p>
          {initialSkills.includes("B1") && !skills.includes("B1") ? <Alert tone="warning">B1 yetkisini kaldırarak kaydetmeniz mevcut uçak yetkinliklerinizi de kaldırır.</Alert> : null}
          {profile?.aircraftPackages.length ? <p>Mevcut uçak tipleriniz: {profile.aircraftPackages.map((item) => item.label).join(", ")}</p> : null}
          {skills.includes("B1") ? <>
            <SearchableCombobox label="Herhangi bir uçak tipi yetkiniz var mı?" multiple value={packageIds} options={AIRCRAFT_QUALIFICATION_PACKAGES.filter((item) => !profile?.aircraftPackages.some((saved) => saved.packageId === item.id)).map((item) => ({ value: item.id, label: item.label }))} onChange={(value) => setPackageIds(value as string[])} placeholder="Uçak ve motor tiplerini seçin" />
            <TextareaField label="Yetki kısıtlama notu (isteğe bağlı)" value={restrictionNote} maxLength={500} onChange={(event) => setRestrictionNote(event.target.value)} />
          </> : null}
        </fieldset>
        <fieldset className="onboarding-fields" disabled={loading || !profile || !catalogs}>
          <legend>Herhangi bir ülke için vizeniz var mı? (isteğe bağlı)</legend>
          <p className="muted">Varsa ülkesini ve tarihlerini ekleyin. Vizeniz yoksa veya şu an paylaşmak istemiyorsanız bu bölümü boş bırakabilirsiniz.</p>
          {visas.map((visa, index) => <div className="onboarding-visa form-stack" key={visa.id ?? index}>
            <SearchableCombobox label="Vize ülkesi" value={visa.countryCode} options={(catalogs?.countries ?? []).filter((country) => country.code !== "TR").map((country) => ({ value: country.code, label: country.name }))} onChange={(value) => updateVisa(index, { countryCode: value as string })} placeholder="Ülke seçin" />
            <div className="form-grid">
              <Field label="Vize başlangıç tarihi (isteğe bağlı)" type="date" value={visa.validFrom ?? ""} max={visa.validUntil || undefined} onChange={(event) => updateVisa(index, { validFrom: event.target.value || null })} />
              <Field label="Vize bitiş tarihi" type="date" value={visa.validUntil} min={visa.validFrom || undefined} onChange={(event) => updateVisa(index, { validUntil: event.target.value })} />
              <SelectField label="Giriş türü" value={visa.entryType} onChange={(event) => updateVisa(index, { entryType: event.target.value as "single" | "multiple" })}><option value="multiple">Çok girişli</option><option value="single">Tek girişli</option></SelectField>
            </div>
            <TextareaField label="Vize kısıtlama notu (isteğe bağlı)" value={visa.restrictionNote ?? ""} maxLength={500} onChange={(event) => updateVisa(index, { restrictionNote: event.target.value || null })} />
            <Button type="button" variant="ghost" onClick={() => { setVisasChanged(true); setVisas((current) => current.filter((_, i) => i !== index)); }}><Trash2 size={17} /> Vizeyi kaldır</Button>
          </div>)}
          <Button type="button" variant="secondary" disabled={visas.length >= 50} onClick={() => { setVisasChanged(true); setVisas((current) => [...current, { countryCode: "", validUntil: "", entryType: "multiple" }]); }}><Plus size={17} /> Vize ekle</Button>
        </fieldset>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <p className="muted">Kaydettiğinizde veya atladığınızda bu ekran tekrar gösterilmez. Atlamak bu formdaki kaydedilmemiş değişiklikleri bırakır.</p>
        <div className="modal-actions">
          <Button id="onboarding-skip" type="button" variant="ghost" disabled={loading} onClick={() => void submit(undefined, true)}>Şimdilik atla</Button>
          <Button type="submit" loading={loading} disabled={!profile || !catalogs}>Kaydet ve devam et</Button>
        </div>
      </form>
    </section>
  </main>;
}
